import React from "react";
import { cn } from "@/libs/utils";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";

/**
 * 구매 레일 아코디언 공용 스타일/헤더.
 *
 * 공통 `v2-components/ui/accordion` 은 FAQ·체크아웃·GNB 등 **20곳이 공유**하므로
 * 기본값을 못 건드린다. 레일에 필요한 무효화를 여기 모아 두고 호출처에서만 덮는다.
 * ([slug].tsx 와 ProductGameKeys 두 곳이 쓴다 — 한쪽만 고쳐져 갈라지는 걸 막으려고 분리)
 *
 * 피그마: 레일 28715:139500, 헤더 28715:139567
 */

// 새 레일은 **구분선 없이 48px 여백만으로** 섹션을 나눈다.
// ⚠️ `last:border-b` 는 variant 가 달라 `border-0` 만으로는 안 지워진다 — tailwind-merge 는
// 같은 variant 안에서만 충돌을 해소한다(pitfalls #8). 안 끄면 마지막 항목 밑줄이 남는다.
/**
 * 레일 섹션 간 간격 — **모바일 64 / 데스크톱 48**.
 * 모바일 프레임(28707:130754)의 Body 섹션이 일관되게 64, 데스크톱 `Right`(28715:139500)는 48.
 *
 * ⚠️ 세 곳이 같은 값을 써야 한 레일 안에서 리듬이 갈리지 않는다 —
 * `#buy-rail` · 레일 아코디언 · `ProductOptionSelect`(색상/사이즈).
 * 문자열로 흩어놓으면 `gap-16` grep 이 페이지의 무관한 컬럼 간격까지 잡아
 * 일부만 고쳐진다(이 모듈이 생긴 이유와 같은 문제).
 */
export const RAIL_SECTION_GAP = "gap-16 laptop:gap-12";

export const RAIL_ACCORDION_ITEM = "border-0 last:border-b-0";

// 헤더 타이포는 Title/Large 22px/28 **w400**(28715:139569). 공통 트리거 기본값은
// `text-base font-500` + `px-4 py-[17px]` + hover 배경이라 전부 덮는다.
// 패딩이 남아 있으면 섹션 간격이 48 이 아니라 60+ 로 벌어진다.
export const RAIL_ACCORDION_TRIGGER =
  "px-0 py-0 text-2xl font-400 hover:bg-transparent";

// 헤더에 보조줄(빨간 안내)이 붙는 섹션은 트리거 안이 2줄 블록이 된다. 공통 cva 의
// 회전 규칙이 `[&[data-state=open]>svg]`(직계 자식)라 쉐브론이 중첩되면 안 돌아간다
// → descendant 셀렉터를 덧붙인다(둘 다 남아도 무해).
export const RAIL_ACCORDION_TRIGGER_WITH_CAPTION = cn(
  RAIL_ACCORDION_TRIGGER,
  "[&[data-state=open]_svg]:-rotate-180",
);

// 헤더 → 콘텐츠 20px, 아래 0. 피그마 각 섹션에서 Lists 가 섹션 바닥에 딱 맞는다
// (④ 72+524=596, ⑦ 48+462=510, ⑨ 48+88=136). 공통 기본값 `p-4` 를 덮는다.
export const RAIL_ACCORDION_CONTENT = "px-0 pb-0 pt-5";

/**
 * 레일 아코디언 헤더 — (제목 + 쉐브론) / 보조줄 2줄, gap 8 (총 52px).
 *
 * 보조줄이 **헤더 안**이라 접힌 상태에서도 미선택 경고가 보인다. 콘텐츠 안에 두면
 * 아코디언을 열어야 보여서 경고 역할을 못 한다.
 */
export function RailSectionHeader({
  title,
  caption,
}: {
  title: React.ReactNode;
  /** 미선택 경고 등. 없으면 헤더는 한 줄(28px). */
  caption?: React.ReactNode;
}) {
  return (
    // ⚠️ `text-left` 필수 — 트리거가 `<button>` 이라 UA 기본값 `text-align: center`
    // 가 상속된다. 제목 줄은 flex 로 배치돼 안 드러나지만, 보조줄은 전폭 박스 안에서
    // 가운데 정렬돼 보인다(실측 x=0 w=464 인데 텍스트만 중앙).
    <span className="flex w-full flex-col gap-2 text-left">
      <span className="flex items-center justify-between">
        {title}
        <ChevronDownIcon className="size-6 shrink-0" />
      </span>
      {!!caption && (
        // Label/Medium 12/16 w500 tracking .5 red-60 (28715:139571)
        <span className="text-sm font-500 tracking-[0.5px] text-red-60">
          {caption}
        </span>
      )}
    </span>
  );
}
